import React, { useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  Alert, type AlertProps, Link, scrollIntoView as scrollIntoViewFn,
} from '@printdeal/ui-library';
import CustomerCareDropdown from './CustomerCareDropdown';
import { SentryHelper, Exception } from '../../helpers/SentryHelper';

interface Props {
  children?: React.ReactNode;
  onRetry?: () => void;
  scrollIntoView?: boolean;
  enableDismiss?: boolean;
  sentryException?: Exception;
  /**
   * If true, show a logging debug ID to the user. Make sure that either `sentryException` is provided or a Sentry
   * exception is logged from the parent component. Only relevant if `showNextSteps` is also true
   */
  showDebugId: boolean;
  showNextSteps?: boolean;
  className?: string;
  variant?: AlertProps['variant'];
}

const ErrorAlert = ({
  children,
  onRetry,
  scrollIntoView = true,
  enableDismiss = false,
  sentryException,
  showDebugId,
  showNextSteps = true,
  className = 'pd-my-3',
  variant = 'error',
}: Props) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollIntoView && ref.current) {
      scrollIntoViewFn(ref.current, {
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
    }
  }, [scrollIntoView]);

  useEffect(() => {
    if (sentryException) {
      SentryHelper.exception(sentryException);
    }
  }, [sentryException]);

  return (
    <div ref={ref} className={className}>
      <Alert variant={variant} isDismissible={enableDismiss} className="pd-mb-3 pd-text-left">
        {children || (
          <FormattedMessage
            id="general.errorBoundary.v2.header"
            defaultMessage="We're sorry! Something went wrong"
          />
        )}
        {(typeof onRetry === 'function' || showNextSteps) && (
          <div>
            {typeof onRetry === 'function' && (
              /* eslint-disable-next-line jsx-a11y/anchor-is-valid */
              <Link
                as="button"
                type="button"
                variant="secondary"
                className="pd-mt-3"
                onClick={onRetry}
                iconBefore="refresh"
              >
                <FormattedMessage
                  id="general.errorBoundary.v2.option.try_again.header"
                  defaultMessage="Try again"
                />
              </Link>
            )}
          </div>
        )}
      </Alert>
      {showNextSteps && <CustomerCareDropdown showDebugId={showDebugId} />}
    </div>
  );
};

export default ErrorAlert;
